import { useState, useEffect, useRef } from "react";
import OutboundCallPanel from "@/components/OutboundCallPanel";
import PlaygroundTranscript from "@/components/PlaygroundTranscript";
import type { Item } from "@/components/types";
import { useNavigate } from "react-router-dom";
import { useCompaniesQuery } from "@/services/companyQueries";
import { usePlaygroundStore } from "@/stores/playgroundStore";
import { useAuth } from "@/hooks/useAuth";
import { initiateCall, getCallStatus, endCall } from "@/services/outboundCallApi";
import { toast } from "sonner";

type CallStatus =
  | "idle"
  | "initiating"
  | "ringing"
  | "in-progress"
  | "completed"
  | "failed"
  | "busy"
  | "no-answer";

export function OutboundCallPage() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [items, _setItems] = useState<Item[]>([]);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const setWorkletsLoaded = usePlaygroundStore((s) => s.setWorkletsLoaded);
  const selectedCompanyId = usePlaygroundStore((s) => s.selectedCompanyId);
  const setSelectedCompanyId = usePlaygroundStore(
    (s) => s.setSelectedCompanyId
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const outputProcessorNodeRef = useRef<AudioWorkletNode | null>(null);
  const callStartTimeRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusPollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const { data: companies = [], isLoading: companiesLoading } =
    useCompaniesQuery();
  const { localUser } = useAuth();

  // Ensure default company selection
  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  // Load audio worklets
  useEffect(() => {
    const loadWorklets = async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }
        const audioContext = audioContextRef.current;
        await audioContext.audioWorklet.addModule("/output-audio-processor.js");
        setWorkletsLoaded(true);
      } catch (err) {
        console.error("Error loading worklets:", err);
      }
    };
    loadWorklets();
  }, []);

  // Call duration timer
  useEffect(() => {
    if (callStatus === "in-progress" && callStartTimeRef.current) {
      durationIntervalRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          setCallDuration(
            Math.floor((Date.now() - callStartTimeRef.current) / 1000)
          );
        }
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [callStatus]);

  // Poll call status
  useEffect(() => {
    if (callSid && ["initiating", "ringing", "in-progress"].includes(callStatus)) {
      statusPollIntervalRef.current = setInterval(async () => {
        try {
          const status = await getCallStatus(callSid);
          updateCallStatus(status.status);
        } catch (err) {
          console.error("Error polling call status:", err);
        }
      }, 2000);
    } else {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
    }

    return () => {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
      }
    };
  }, [callSid, callStatus]);

  const updateCallStatus = (twilioStatus: string) => {
    switch (twilioStatus) {
      case "initiated":
        setCallStatus("initiating");
        break;
      case "ringing":
        setCallStatus("ringing");
        break;
      case "in-progress":
        if (callStatus !== "in-progress") {
          setCallStatus("in-progress");
          callStartTimeRef.current = Date.now();
        }
        break;
      case "completed":
        setCallStatus("completed");
        cleanupCall();
        break;
      case "busy":
        setCallStatus("busy");
        cleanupCall();
        break;
      case "no-answer":
        setCallStatus("no-answer");
        cleanupCall();
        break;
      case "failed":
        setCallStatus("failed");
        cleanupCall();
        break;
    }
  };

  const handleInitiateCall = async () => {
    if (!selectedCompanyId || !localUser?.id) {
      toast.error("Please select a company and ensure you're logged in", {
        position: "bottom-right",
      });
      return;
    }

    setError(null);
    setCallStatus("initiating");

    try {
      const result = await initiateCall({
        toNumber: phoneNumber,
        companyId: selectedCompanyId,
        userId: localUser.id,
      });

      setCallSid(result.callSid);
      setSessionId(result.sessionId);
      updateCallStatus(result.status);

      toast.success(`Call Initiated to ${phoneNumber}`, {
        position: "bottom-right",
      });

      // Connect to WebSocket for transcript monitoring (read-only)
      // Note: This would require backend support for monitor-only connections
      // For now, transcripts can be viewed after the call via session details
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initiate call"
      );
      setCallStatus("idle");
      toast.error(err instanceof Error ? err.message : "Failed to initiate call", {
        position: "bottom-right",
      });
    }
  };

  const handleEndCall = async () => {
    if (!callSid) return;

    try {
      await endCall(callSid);
      setCallStatus("completed");
      cleanupCall();
      toast.success("Call ended", {
        position: "bottom-right",
      });
    } catch (err) {
      console.error("Error ending call:", err);
      toast.error("Failed to end call", {
        position: "bottom-right",
      });
    }
  };

  const cleanupCall = () => {
    if (ws) {
      ws.close();
      setWs(null);
    }
    if (outputProcessorNodeRef.current) {
      outputProcessorNodeRef.current.disconnect();
      outputProcessorNodeRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (statusPollIntervalRef.current) {
      clearInterval(statusPollIntervalRef.current);
      statusPollIntervalRef.current = null;
    }
    callStartTimeRef.current = null;
  };

  useEffect(() => {
    return () => {
      cleanupCall();
    };
  }, []);

  return (
    <div className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Outbound Call</h1>
        {sessionId && (
          <button
            onClick={() => navigate(`/admin/sessions/${sessionId}`)}
            className="text-sm text-blue-600 hover:underline"
          >
            View Session Details
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OutboundCallPanel
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          callStatus={callStatus}
          onInitiateCall={handleInitiateCall}
          onEndCall={handleEndCall}
          selectedCompanyId={selectedCompanyId}
          setSelectedCompanyId={setSelectedCompanyId}
          companies={companies}
          companiesLoading={companiesLoading}
          callDuration={callDuration}
          error={error}
        />

        <div className="border rounded-lg p-4 bg-card">
          <h3 className="text-lg font-semibold mb-4">Call Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Status:</span>
              <span className="font-medium">{callStatus}</span>
            </div>
            {callSid && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Call SID:</span>
                <span className="font-mono text-xs">{callSid}</span>
              </div>
            )}
            {sessionId && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Session ID:
                </span>
                <span className="font-mono text-xs">{sessionId}</span>
              </div>
            )}
            {callDuration > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                <span className="font-medium">
                  {Math.floor(callDuration / 60)}m {callDuration % 60}s
                </span>
              </div>
            )}
          </div>

          <div className="mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Note: Live transcripts are available in the session details after the
              call connects. Navigate to the session to view real-time transcription.
            </p>
          </div>
        </div>
      </div>

      {/* Transcript section - shown if we implement real-time monitoring */}
      {items.length > 0 && (
        <div className="mt-6">
          <PlaygroundTranscript items={items} />
        </div>
      )}
    </div>
  );
}
